import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const colors = { Todo: "bg-slate-100 text-slate-700", InProgress: "bg-amber-100 text-amber-700", Done: "bg-emerald-100 text-emerald-700" };

export default function MyTasks() {
  const { user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [projects, setProjects] = useState([]);

  const load = async () => {
    const [t, p] = await Promise.all([api.get(`/tasks?assignee_id=${user.id}`), api.get("/projects")]);
    setTasks(t.data); setProjects(p.data);
  };
  useEffect(() => { load(); }, []);

  const update = async (t, changes) => {
    try {
      await api.patch(`/tasks/${t.id}`, changes);
      toast.success("Task updated");
      load();
    } catch (e) { toast.error("Failed"); }
  };

  const projMap = Object.fromEntries(projects.map(p => [p.id, p.name]));

  return (
    <div className="p-6 lg:p-8">
      <div className="mb-6">
        <div className="text-xs uppercase tracking-widest text-slate-500 font-medium">Assignments</div>
        <h1 className="font-display text-3xl font-extrabold mt-1">My Tasks</h1>
        <p className="text-sm text-slate-500 mt-1">All tasks assigned to you across projects.</p>
      </div>
      {tasks.length === 0 ? (
        <Card className="border-dashed"><CardContent className="p-10 text-center text-sm text-slate-500">Nothing on your plate.</CardContent></Card>
      ) : (
        <div className="space-y-2">
          {tasks.map((t) => (
            <Card key={t.id} data-testid={`mytask-${t.id}`}>
              <CardContent className="p-4">
                <div className="flex items-center gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h4 className="font-semibold text-sm truncate">{t.title}</h4>
                      <span className={`status-chip ${colors[t.status] || "bg-slate-100 text-slate-700"}`}>{t.status}</span>
                      <Badge variant="outline" className="text-[10px]">{t.priority}</Badge>
                    </div>
                    <div className="text-xs text-slate-500 mt-1">{projMap[t.project_id] || "—"} {t.due_date && <span className="font-mono ml-2">Due: {t.due_date}</span>}</div>
                  </div>
                  <div className="w-32 shrink-0">
                    <input type="range" min="0" max="100" value={t.progress || 0} onChange={(e) => update(t, { progress: parseInt(e.target.value) })} className="w-full" data-testid={`task-progress-${t.id}`} />
                    <div className="text-[10px] font-mono text-slate-500 text-right">{t.progress || 0}%</div>
                  </div>
                  <Select value={t.status} onValueChange={(v) => update(t, { status: v })}>
                    <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Todo">Todo</SelectItem>
                      <SelectItem value="InProgress">In Progress</SelectItem>
                      <SelectItem value="Done">Done</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
